import { Script, graphql } from "gatsby"
import React from "react"
import { Layout } from "@bw/layouts"
import { Seo, Section, CmsImage } from "@bw/bits"
import { Teasers, SearchPharmacy } from "@bw/modules"
import { Teaser, Breadcrumbs } from "@bw/partials"
import { useTranslation } from "react-i18next"
import shuffle from "../utils/shuffle"
import Event from "../models/event"
import { Content, Header } from "../components/modules/mapModules.jsx"

export const Head = ({ data }) => (
  <>
    {data.collectionEvents.head && (
      <Script
        id={`user-script-${data.collectionEvents.id}`}
        dangerouslySetInnerHTML={{ __html: data.collectionEvents.head }}
      />
    )}
  </>
)

const EventTemplate = ({ data, pageContext, location }) => {
  const { collectionEvents, events, pharmacies, mount } = data
  const event = new Event(collectionEvents)
  const allEvents = shuffle(events.nodes)
  const { t } = useTranslation()
  const breadcrumbsLinks = [
    {
      title: "Accueil",
      to: "/",
    },
    {
      title: event.title,
      to: event.slug,
    },
  ]

  if (mount) {
    breadcrumbsLinks.splice(1, 0, {
      title: mount.title,
      to: mount.slug,
    })
  }

  let dates = event.getDateSpan().reduce((acc, date, i, dates) => {
    if (i > 0) {
      acc += "<br/>"
    }
    acc +=
      dates.length === 2 && i === 0
        ? t("dates:fromDate", { date })
        : dates.length === 2 && i === 1
          ? t("dates:toDate", { date })
          : date

    return acc
  }, "")

  
  const updatedHeader = event.header.map((h, i) => {
    if (i !== 0) return h // ne modifier que le premier module
    return {
      ...h,
      subtitle: `${h.subtitle || ""}${dates}`,
    }
  })

  /**
   * Find pharmacies that offer this event
   */
  let participating = []
  if (pharmacies?.nodes) {
    participating = pharmacies.nodes
      .filter(pharmacy => {
        return pharmacy.events.map(p => p.id).includes(pageContext.id)
      })
      .map(p => ({
        ...p,
        state: p.state?.value,
      }))
  }

  return (
    <Layout color={event.color?.value} {...{ pageContext }}>
      <Seo
        url={location.href}
        title={event.metaTitle || event.title}
        description={event.metaDescription}
        image={event.image?.url}
        meta={[
          {
            name: "robots",
            content: event.doNotIndex
              ? "noindex, nofollow"
              : "max-image-preview:large, index, follow",
          },
        ]}
      />
      <Header modules={updatedHeader} page={event} />
      {breadcrumbsLinks && <Breadcrumbs breadcrumbs={breadcrumbsLinks} />}
      <Content modules={event.content} />
      {participating && participating.length > 0 && (
        <Section margin="100px 0" title={t("participatingPharmacies")}>
          <SearchPharmacy
            pharmacies={participating}
            map={false}
            textSearch={false}
          />
        </Section>
      )}
      {allEvents && allEvents.length > 0 && (
        <Section margin="100px 0" title={t("other_events")}>
          <Teasers>
            {allEvents.map((item, i) => {
              return (
                <Teaser
                  smaller
                  key={i}
                  title={item.title}
                  excerpt={item.teaserExcerpt}
                  to={item.slug || item.uri}
                  image={
                    item.teaserImage ? (
                      <CmsImage src={item.teaserImage} alt={item.title} />
                    ) : null
                  }
                />
              )
            })}
          </Teasers>
        </Section>
      )}
    </Layout>
  )
}

export default EventTemplate

export const eventQuery = graphql`
  query eventQuery($id: String!) {
    collectionEvents(id: { eq: $id }) {
      id
      head
      slug
      title
      pictogram {
        value
      }
      metaDescription
      metaTitle
      image {
        url
      }
      doNotIndex
      color {
        value
      }
      teaserExcerpt
      teaserImage {
        localImage {
          childImageSharp {
            gatsbyImageData(width: 254, height: 139)
          }
        }
      }
      startDate
      startTime
      endDate
      endTime
      header {
        __typename
        suptitle
        title
        subtitle

        ... on PageHeaderModule {
          ...PageHeaderModuleFragment
        }
        ... on HeroModule {
          ...HeroModuleFragment
        }
      }
      content {
        __typename
        suptitle
        title
        subtitle
        buttonType {
          value
        }
        buttonUrl
        buttonIsExternal
        buttonText
        buttonPosition {
          value
        }
        buttonEntry {
          url
        }
        ancre

        ...CallToActionModuleFragment
        ...ContentModuleFragment
        ...ContentWithImageModuleFragment
        ...GalleryModuleFragment
        ...ContentOnColumnsModuleFragment
        ...TeasersLatestModuleFragment
        ...LogoGridModuleFragment
        ...ImageModuleFragment
        ...VideoModuleFragment
        ...DownloadsModuleFragment
      }
    }
    pharmacies: allCollectionPharmacies {
      nodes {
        id
        events {
          id
        }
        title
        slug
        street
        zipCode
        city
        state {
          value
        }
        latitude
        longitude
      }
    }
    events: allCollectionEvents(filter: { id: { ne: $id } }) {
      nodes {
        title
        teaserExcerpt
        slug
        teaserImage {
          localImage {
            childImageSharp {
              gatsbyImageData(width: 351, height: 175)
            }
          }
        }
      }
    }
    mount: collectionPages(mount: { handle: { eq: "events" } }) {
      slug
      title
      header {
        ... on PageHeaderModule {
          image {
            url
          }
        }
      }
    }
  }
`
